/**
 * @file AccountDetailsDrawer.js
 * @description Side drawer component that displays population groups for an individual.
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { fetchMemberOf } from '../../../services/populationGroupService';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import style from '../IndividualPage.module.scss';
import { getText } from '../../../i18n';

const AccountDetailsDrawer = ({ isOpen, closeDrawer, individualId }) => {
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getMemberData = async () => {
            setIsLoading(true);
            try {
                const data = await fetchMemberOf(individualId);
                setMembers(data);
            } catch (error) {
                console.error('Failed to fetch member data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen && individualId) {
            getMemberData();
        }
    }, [isOpen, individualId]);

    const handleOverlayClick = () => {
        closeDrawer();
    };

    const handleDrawerClick = event => {
        event.stopPropagation();
    };

    const handleKeyDown = event => {
        if (event.key === 'Escape') {
            closeDrawer();
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const drawerContent = (
        <>
            <div
                className={style.accountDrawerOverlay}
                onClick={handleOverlayClick}
                role="presentation"
            />
            <div
                className={style.accountDrawer}
                onClick={handleDrawerClick}
                role="dialog"
                aria-modal="true"
                aria-labelledby="drawer-title"
            >
                <div className={style.accountDrawerHeader}>
                    <h5 id="drawer-title" className={style.accountDrawerTitle}>
                        {getText('msg_a951f4d0a0fa')}
                    </h5>
                    <button
                        type="button"
                        className={style.accountDrawerCloseButton}
                        onClick={closeDrawer}
                        aria-label={getText('msg_de4e7f6fad95')}
                    >
                        <i className="bi bi-x me-1" />
                        {getText('msg_bbfa773e5a63')}
                    </button>
                </div>

                <div className={style.accountDrawerContent}>
                    <h6 className={style.accountDrawerSectionTitle}>
                        {getText('msg_e744dd6b3c48')}
                    </h6>
                    <p className={style.accountDrawerDescription}>
                        {getText('msg_e4cd6d067c46')}
                    </p>

                    <label className={style.accountDrawerLabel}>
                        {getText('msg_3fc6dbe69ceb')}
                    </label>

                    <LoadingWrapper isLoading={isLoading}>
                        <div className={style.boxAccountDrawerBadgeContainer}>
                            <div className={style.accountDrawerBadgeContainer}>
                                {!isLoading && members.length === 0 && (
                                    <span className="text-muted">
                                        {getText('msg_430fa51257e7')}
                                    </span>
                                )}

                                {!isLoading &&
                                    members.map(member => (
                                        <span
                                            key={member.id || member.name}
                                            className="badge-base badge-population-group"
                                        >
                                            {member.name}
                                        </span>
                                    ))}
                            </div>
                        </div>
                    </LoadingWrapper>
                </div>
            </div>
        </>
    );

    return ReactDOM.createPortal(drawerContent, document.body);
};

AccountDetailsDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    closeDrawer: PropTypes.func.isRequired,
    individualId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AccountDetailsDrawer;
